/**
 * PreintegratedSkin — Penner SIGGRAPH 2011 pre-integrated skin scattering:
 * the diffusion of light across curved skin is baked into a 2D LUT indexed
 * by (N·L, curvature 1/r); shading becomes one texture fetch — no blur
 * passes. This is the cheap-tier companion to the engine's separable
 * screen-space SSS.
 *
 * The bake integrates the diffuse response of a ring of radius r lit
 * directionally, weighting by the d'Eon/Luebke skin diffusion profile
 * (sum-of-Gaussians, red scattering farthest) — producing the signature red
 * terminator shift at high curvature.
 *
 * Application here is the documented v1: the LUT's scatter tint (LUT − the
 * un-scattered wrap response) is added via emissive against a key-light
 * direction uniform; a full lighting-model override is the v2.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, vec3, texture, normalView, normalize, saturate, vec2,
} from 'three/tsl';

// ─── Diffusion profile (d'Eon-style sum of Gaussians, mm units) ────────────

const PROFILE: Array<{ variance: number; weight: [number, number, number] }> = [
  { variance: 0.0064, weight: [0.233, 0.455, 0.649] },
  { variance: 0.0484, weight: [0.100, 0.336, 0.344] },
  { variance: 0.187, weight: [0.118, 0.198, 0.000] },
  { variance: 0.567, weight: [0.113, 0.007, 0.007] },
  { variance: 1.99, weight: [0.358, 0.004, 0.000] },
  { variance: 7.41, weight: [0.078, 0.000, 0.000] },
];

/**
 * Skin diffusion profile R(d) per channel — d'Eon & Luebke's sum of NORMALISED
 * 2D Gaussians G(v, r) = e^(−r²/2v) / (2πv) (GPU Gems 3 ch. 14: "unit impulse
 * response"; the weights per channel sum to 1, so ∫R dA = 1 — a white profile).
 * WHY (2026-09-29): this returned the Gaussians WITHOUT the 1/(2πv) — each term
 * peaked at its weight, so the 7.41 mm² red lobe carried ~350× its share of
 * energy against the 0.0064 mm² one and every red terminator was far too wide.
 */
export function skinProfileCpu(dist: number): [number, number, number] {
  let r = 0; let g = 0; let b = 0;
  for (const { variance, weight } of PROFILE) {
    const gauss = Math.exp(-(dist * dist) / (2 * variance)) / (2 * Math.PI * variance);
    r += weight[0] * gauss;
    g += weight[1] * gauss;
    b += weight[2] * gauss;
  }
  return [r, g, b];
}

/**
 * The profile's 1D marginal along a line (mm⁻¹): Σ wᵢ · N(x; σᵢ²=vᵢ). A 2D
 * Gaussian integrated across one axis is the 1D Gaussian of the same variance,
 * so this is the scattering a RING (a cylinder lit uniformly along its axis)
 * sees — the form NVIDIA FaceWorks integrates (precomp.cpp
 * `EvaluateDiffusionProfile`). Integrates to 1 per channel.
 */
export function skinProfile1dCpu(x: number): [number, number, number] {
  let r = 0; let g = 0; let b = 0;
  for (const { variance, weight } of PROFILE) {
    const gauss = Math.exp(-(x * x) / (2 * variance)) / Math.sqrt(2 * Math.PI * variance);
    r += weight[0] * gauss; g += weight[1] * gauss; b += weight[2] * gauss;
  }
  return [r, g, b];
}

/**
 * Transmittance through a slab `dMm` thick — Jimenez et al. 2010, "Real-Time
 * Realistic Skin Translucency" (the `SSSSTransmittance` profile of the
 * separable-SSS reference code): T(d) = Σ wᵢ e^(−d²/vᵢ). T(0) = 1 per channel
 * and red survives farthest (the backlit-ear glow).
 */
export function skinTransmittanceCpu(dMm: number): [number, number, number] {
  let r = 0; let g = 0; let b = 0;
  const dd = dMm * dMm;
  for (const { variance, weight } of PROFILE) {
    const e = Math.exp(-dd / variance);
    r += weight[0] * e; g += weight[1] * e; b += weight[2] * e;
  }
  return [r, g, b];
}

/**
 * Ring-integrated diffuse for cos(θ)=nDotL at ring radius r (CPU reference —
 * Penner 2011's pre-integration, with saturate(cos) over the WHOLE ring, his
 * erratum slide).
 *
 * D(θ, r) = Σᵢ wᵢ ∫ sat(cos(θ + s/r)) N(s; σᵢ) ds  /  Σᵢ wᵢ ∫ N(s; σᵢ) ds
 *
 * over the arc length s ∈ ±min(πr, 5σᵢ), one midpoint rule PER GAUSSIAN
 * (`steps` samples each). WHY (2026-09-29): the old version sampled the ring
 * ANGLE uniformly, so the 0.08 mm lobe was missed entirely beyond r ≈ 5 mm (the
 * nearest sample sat a millimetre away) and a flat surface never returned to
 * Lambert; it also weighted the ring by the 2D profile, which over-weights
 * narrow lobes — the 1D marginal is the ring's own profile. D → sat(cos θ) as
 * r → ∞ and ∫D dθ = ∫sat(cos θ) dθ (a normalised blur).
 */
export function preintegratedDiffuseCpu(
  nDotL: number, radiusMm: number, steps = 80,
): [number, number, number] {
  const theta = Math.acos(Math.min(Math.max(nDotL, -1), 1));
  const r = Math.max(radiusMm, 1e-3);
  const acc: [number, number, number] = [0, 0, 0];
  const norm: [number, number, number] = [0, 0, 0];
  for (const { variance, weight } of PROFILE) {
    const sigma = Math.sqrt(variance);
    const half = Math.min(Math.PI * r, 5 * sigma);
    const ds = (2 * half) / steps;
    let lit = 0; let mass = 0;
    for (let i = 0; i < steps; i++) {
      const s = -half + (i + 0.5) * ds;
      const g = Math.exp(-(s * s) / (2 * variance));
      lit += Math.max(Math.cos(theta + s / r), 0) * g;
      mass += g;
    }
    // the common 1/(σ√2π)·ds cancels inside one Gaussian; across Gaussians its mass ratio is ∫N over the (possibly
    // truncated) window — `mass·ds/(σ√2π)`
    const k = ds / (sigma * Math.sqrt(2 * Math.PI));
    for (let c = 0; c < 3; c++) { acc[c] += weight[c] * lit * k; norm[c] += weight[c] * mass * k; }
  }
  return [acc[0] / Math.max(norm[0], 1e-9), acc[1] / Math.max(norm[1], 1e-9), acc[2] / Math.max(norm[2], 1e-9)];
}

/** How a curvature (1/r, mm⁻¹) maps onto the curvature LUT's V axis (sqrt: most skin is gently curved). */
export interface SkinCurvatureLutConfig {
  /** N·L columns (U: N·L ∈ [−1, 1] → [0, 1]). Default 64. */
  width?: number;
  /** curvature rows (V). Default 64. */
  height?: number;
  /** the tightest radius represented (V = 1). Default 1 mm (FaceWorks: 0.1–10 cm for faces). */
  rMinMm?: number;
  /** the flattest radius represented (V = 0). Default 100 mm. */
  rMaxMm?: number;
  /** quadrature samples per Gaussian. Default 80. */
  steps?: number;
  /** renormalise each row so the WHITE FURNACE holds exactly: ∫D dω = π per channel. Default true. */
  energyNormalize?: boolean;
}

export interface SkinCurvatureLut {
  /** RGBA float32, row-major (row 0 = V 0 = the flattest), holding D − saturate(N·L) (the SCATTER DELTA; A = 1). */
  data: Float32Array;
  width: number;
  height: number;
  curvatureMin: number;
  curvatureMax: number;
  /** per row, per channel: 2∫D dμ BEFORE the renormalisation (1 = energy-neutral; Lambert integrates to 1). */
  energy: Array<[number, number, number]>;
}

/** V coordinate of a curvature (mm⁻¹) — the sqrt of its place between 1/rMax and 1/rMin, clamped. */
export function skinLutV(curvaturePerMm: number, cfg: Pick<SkinCurvatureLutConfig, 'rMinMm' | 'rMaxMm'> = {}): number {
  const cMin = 1 / (cfg.rMaxMm ?? 100), cMax = 1 / (cfg.rMinMm ?? 1);
  const t = (curvaturePerMm - cMin) / (cMax - cMin);
  return Math.sqrt(Math.min(1, Math.max(0, t)));
}

/** The curvature (mm⁻¹) a LUT row stands for — the inverse of `skinLutV`. */
export function skinLutCurvature(v: number, cfg: Pick<SkinCurvatureLutConfig, 'rMinMm' | 'rMaxMm'> = {}): number {
  const cMin = 1 / (cfg.rMaxMm ?? 100), cMax = 1 / (cfg.rMinMm ?? 1);
  return cMin + v * v * (cMax - cMin);
}

/**
 * Bake the (N·L, curvature) pre-integrated LUT as the SCATTER DELTA
 * D − saturate(N·L) (FaceWorks stores the same delta, so per-channel bent
 * normals can add their own N·L). Texel centres: U = (x + ½)/W ↔ N·L = 2U − 1,
 * V = (y + ½)/H ↔ curvature via `skinLutCurvature`. Pure data (no GPU object).
 */
export function bakeSkinCurvatureLut(config: SkinCurvatureLutConfig = {}): SkinCurvatureLut {
  const width = config.width ?? 64, height = config.height ?? 64, steps = config.steps ?? 80;
  const data = new Float32Array(width * height * 4);
  const energy: Array<[number, number, number]> = [];
  const norm = config.energyNormalize !== false;
  for (let y = 0; y < height; y++) {
    const c = skinLutCurvature((y + 0.5) / height, config);
    const radius = 1 / Math.max(c, 1e-6);
    const row: Array<[number, number, number]> = [];
    const e: [number, number, number] = [0, 0, 0];
    for (let x = 0; x < width; x++) {
      const nDotL = ((x + 0.5) / width) * 2 - 1;
      const d = preintegratedDiffuseCpu(nDotL, radius, steps);
      row.push(d);
      // 2∫D dμ by the midpoint rule over the texel centres (dμ = 2/W): Lambert → 1
      for (let k = 0; k < 3; k++) e[k] += d[k] * (4 / width);
    }
    // the same midpoint rule applied to saturate(μ) — its exact value is 1, the discrete one differs by O(1/W²)
    let lam = 0; for (let x = 0; x < width; x++) lam += Math.max(0, ((x + 0.5) / width) * 2 - 1) * (4 / width);
    const ratio: [number, number, number] = [e[0] / lam, e[1] / lam, e[2] / lam];
    energy.push(ratio);
    for (let x = 0; x < width; x++) {
      const nDotL = ((x + 0.5) / width) * 2 - 1;
      const sat = Math.max(0, nDotL);
      const i = (y * width + x) * 4;
      for (let k = 0; k < 3; k++) data[i + k] = (norm ? row[x][k] / ratio[k] : row[x][k]) - sat;
      data[i + 3] = 1;
    }
  }
  return { data, width, height, curvatureMin: 1 / (config.rMaxMm ?? 100), curvatureMax: 1 / (config.rMinMm ?? 1), energy };
}

export interface SkinLutResult {
  texture: THREE.DataTexture;
  size: number;
}

/**
 * Bake the (N·L, curvature) LUT. U: N·L remapped [−1,1]→[0,1]; V: skin
 * radius from `maxRadiusMm` (V=1 → tight curvature, small radius).
 */
export function bakeSkinLut(size = 64, maxRadiusMm = 16): SkinLutResult {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    // V row → ring radius: high V = high curvature (small r).
    const radius = maxRadiusMm * (1 - y / (size - 1)) + 0.4;
    for (let x = 0; x < size; x++) {
      const nDotL = (x / (size - 1)) * 2 - 1;
      const [r, g, b] = preintegratedDiffuseCpu(nDotL, radius, 64);
      const i = (y * size + x) * 4;
      data[i] = Math.min(255, Math.round(r * 255));
      data[i + 1] = Math.min(255, Math.round(g * 255));
      data[i + 2] = Math.min(255, Math.round(b * 255));
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  tex.name = 'PreintegratedSkinLUT';
  return { texture: tex, size };
}

export interface PreintegratedSkinConfig {
  /** Pre-baked LUT (bakeSkinLut() when omitted). */
  lut?: THREE.DataTexture;
  /** Surface curvature 0..1 (V axis of the LUT). Default 0.5. */
  curvature?: number;
  /** Key light direction in view space (default sun-ish). */
  lightDirView?: [number, number, number];
  /** Scatter tint strength (default 0.5). */
  intensity?: number;
}

export interface PreintegratedSkinHandle {
  curvature: { value: number };
  lightDirView: { value: THREE.Vector3 };
  intensity: { value: number };
  lut: THREE.DataTexture;
}

/** Add the LUT's scatter tint (red terminator) to a skin material. */
export function applyPreintegratedSkin(
  material: THREE.Material & { emissiveNode?: unknown },
  config?: PreintegratedSkinConfig,
): PreintegratedSkinHandle {
  const lut = config?.lut ?? bakeSkinLut().texture;
  const uCurvature = uniform(config?.curvature ?? 0.5);
  const ld = config?.lightDirView ?? [0.3, 0.5, 0.8];
  const uLightDir = uniform(vec3(ld[0], ld[1], ld[2]));
  const uIntensity = uniform(config?.intensity ?? 0.5);

  const prevEmissive = material.emissiveNode;
  material.emissiveNode = Fn(() => {
    const nDotL = normalView.dot(normalize(uLightDir));
    const u = nDotL.mul(0.5).add(0.5);
    const scattered = vec3(texture(lut, vec2(u, uCurvature)).rgb);
    // Scatter TINT = pre-integrated response minus the hard clamped lambert:
    // only the diffusion excess (the red bleed past the terminator) is added.
    const hard = saturate(nDotL);
    const tint = scattered.sub(vec3(hard, hard, hard)).max(vec3(0, 0, 0));
    const base = (prevEmissive ?? vec3(0, 0, 0)) as never;
    return vec3(base).add(tint.mul(uIntensity));
  })();
  material.needsUpdate = true;

  return {
    curvature: uCurvature as unknown as { value: number },
    lightDirView: uLightDir as unknown as { value: THREE.Vector3 },
    intensity: uIntensity as unknown as { value: number },
    lut,
  };
}
